import PropTypes from 'prop-types';
import React from 'react';

import Box from '../box/box.jsx';
import BufferedInputHOC from '../forms/buffered-input-hoc.jsx';
import Input from '../forms/input.jsx';

import {injectIntl, intlShape, defineMessages, FormattedMessage} from 'react-intl';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

import styles from './sprite-info-bar.css';

const BufferedInput = BufferedInputHOC(Input);

const messages = defineMessages({
    spriteName: {
        id: 'gui.SpriteInfo.spriteName',
        defaultMessage: 'Sprite Name',
        description: 'Label for sprite name field'
    },
    spritePlaceholder: {
        id: 'gui.SpriteInfo.spritePlaceholder',
        defaultMessage: 'Name',
        description: 'Placeholder text for sprite name'
    },
    showSpriteAction: {
        id: 'gui.SpriteInfo.showSpriteAction',
        defaultMessage: 'Show sprite',
        description: 'Tooltip for show sprite button'
    },
    hideSpriteAction: {
        id: 'gui.SpriteInfo.hideSpriteAction',
        defaultMessage: 'Hide sprite',
        description: 'Tooltip for hide sprite button'
    }
});

class SpriteInfoBar extends React.Component {
    shouldComponentUpdate (nextProps) {
        return (
            this.props.disabled !== nextProps.disabled ||
            this.props.name !== nextProps.name ||
            this.props.stageMode !== nextProps.stageMode ||
            this.props.visible !== nextProps.visible ||
            // Only update these if rounded value has changed
            Math.round(this.props.direction || 0) !== Math.round(nextProps.direction || 0) ||
            Math.round(this.props.size || 0) !== Math.round(nextProps.size || 0) ||
            Math.round(this.props.x || 0) !== Math.round(nextProps.x || 0) ||
            Math.round(this.props.y || 0) !== Math.round(nextProps.y || 0)
        );
    }

    render () {
        if (this.props.disabled) {
            return null;
        }

        // ステージモードの場合はシンプル表示
        if (this.props.stageMode) {
            return (
                <Box className={styles.spriteInfoBar}>
                    <div className={styles.nameSection}>
                        <div className={styles.fieldGroup}>
                            <span className={styles.fieldLabel}>
                                選択中
                            </span>
                            <div className={styles.stageDisplay}>
                                {this.props.name}
                            </div>
                        </div>
                    </div>
                </Box>
            );
        }

        return (
            <Box className={styles.spriteInfoBar}>
                <div className={styles.nameSection}>
                    <div className={styles.fieldGroup}>
                        <span className={styles.fieldLabel}>
                            {this.props.intl.formatMessage(messages.spriteName)}
                        </span>
                        <BufferedInput
                            className={styles.fieldInput}
                            disabled={this.props.disabled}
                            placeholder={this.props.intl.formatMessage(messages.spritePlaceholder)}
                            tabIndex="0"
                            type="text"
                            value={this.props.disabled ? '' : this.props.name}
                            onSubmit={this.props.onChangeName}
                        />
                    </div>
                </div>

                <div className={styles.fieldsSection}>
                    <div className={styles.fieldGroup}>
                        <span className={styles.fieldLabel}>x</span>
                        <BufferedInput
                            className={styles.fieldInput}
                            disabled={this.props.disabled}
                            placeholder="x"
                            tabIndex="0"
                            type="text"
                            value={this.props.disabled ? '' : Math.round(this.props.x)}
                            onSubmit={this.props.onChangeX}
                        />
                    </div>
                    <div className={styles.fieldGroup}>
                        <span className={styles.fieldLabel}>y</span>
                        <BufferedInput
                            className={styles.fieldInput}
                            disabled={this.props.disabled}
                            placeholder="y"
                            tabIndex="0"
                            type="text"
                            value={this.props.disabled ? '' : Math.round(this.props.y)}
                            onSubmit={this.props.onChangeY}
                        />
                    </div>
                    <div className={styles.fieldGroup}>
                        <FormattedMessage
                            defaultMessage="Size"
                            description="Sprite info size label"
                            id="gui.SpriteInfo.size"
                        >
                            {message => (
                                <span className={styles.fieldLabel}>{message}</span>
                            )}
                        </FormattedMessage>
                        <BufferedInput
                            className={styles.fieldInput}
                            disabled={this.props.disabled}
                            tabIndex="0"
                            type="text"
                            value={this.props.disabled ? '' : Math.round(this.props.size)}
                            onSubmit={this.props.onChangeSize}
                        />
                    </div>
                    <div className={styles.fieldGroup}>
                        <FormattedMessage
                            defaultMessage="Direction"
                            description="Sprite info direction label"
                            id="gui.SpriteInfo.direction"
                        >
                            {message => (
                                <span className={styles.fieldLabel}>{message}</span>
                            )}
                        </FormattedMessage>
                        <BufferedInput
                            className={styles.fieldInput}
                            disabled={this.props.disabled}
                            tabIndex="0"
                            type="text"
                            value={this.props.disabled ? '' : Math.round(this.props.direction)}
                            onSubmit={this.props.onChangeDirection}
                        />
                    </div>
                </div>

                <div className={styles.visibilitySection}>
                    <button
                        className={styles.visibilityToggle}
                        disabled={this.props.disabled}
                        onClick={this.props.visible ? this.props.onClickNotVisible : this.props.onClickVisible}
                        title={this.props.visible
                            ? this.props.intl.formatMessage(messages.hideSpriteAction)
                            : this.props.intl.formatMessage(messages.showSpriteAction)
                        }
                    >
                        {this.props.visible ? <FaEye size={12} /> : <FaEyeSlash size={12} />}
                    </button>
                </div>
            </Box>
        );
    }
}

SpriteInfoBar.propTypes = {
    direction: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    disabled: PropTypes.bool,
    intl: intlShape,
    name: PropTypes.string,
    onChangeDirection: PropTypes.func,
    onChangeName: PropTypes.func,
    onChangeSize: PropTypes.func,
    onChangeX: PropTypes.func,
    onChangeY: PropTypes.func,
    onClickNotVisible: PropTypes.func,
    onClickVisible: PropTypes.func,
    size: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    stageMode: PropTypes.bool,
    visible: PropTypes.bool,
    x: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    y: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ])
};

export default injectIntl(SpriteInfoBar);